import type { RouteSeoEntry } from '@/config/seo';

/**
 * The single source of truth for indexable routes. The sitemap, the prerender
 * (out/<path>/index.html) and the client-side meta all read this map, so a route that is
 * missing here is not in the sitemap and is not prerendered.
 * Each title and description is written only from that page's own visible copy (hero,
 * features, benefits). Rules (enforced by config/seo.test.ts): unique title <= 60 chars,
 * unique description 70-160 chars, paths without a trailing slash ("/" for home).
 */
export const seoMeta: Record<string, RouteSeoEntry> = {
  '/': {
    title: 'Chronic Care Management Solutions | iCCM',
    description:
      'Integrative chronic care management combining behavioral medicine, nutrition, physical therapy, pharmacy and internal medicine for better patient outcomes.',
  },
  '/ccm': {
    title: 'Chronic Care Management (CCM) Program | iCCM',
    description:
      'Care coordination for patients with multiple chronic conditions: 24/7 access to a dedicated care team, personalized care plans and continuous monitoring.',
  },
  '/rpm': {
    title: 'Remote Patient Monitoring (RPM) | iCCM',
    description:
      "Bluetooth and 4G devices track blood pressure, blood glucose, weight, SpO2 and more in real time from your patients' homes, with instant clinical alerts.",
  },
  '/awv': {
    title: 'Annual Wellness Visits (AWV) | iCCM',
    description:
      'Annual wellness assessments for Medicare beneficiaries that identify health risks, update care plans and promote long-term wellness.',
  },
  '/bhi': {
    title: 'Behavioral Health Integration (BHI) | iCCM',
    description:
      'Mental health and behavioral care integrated with primary care services to address the whole person and improve overall health outcomes.',
  },
  '/pcm': {
    title: 'Principal Care Management (PCM) | iCCM',
    description:
      'Care coordination for patients with a single high-risk chronic condition that requires complex care management and ongoing clinical support.',
  },
  '/tcm': {
    title: 'Transitional Care Management (TCM) | iCCM',
    description:
      'Support during the transition from hospital to home: coordinated follow-up services that keep care continuous and reduce readmission risk.',
  },
  '/rtm': {
    title: 'Remote Therapeutic Monitoring (RTM) | iCCM',
    description:
      'Remote digital monitoring of adherence to medication, respiratory therapy, musculoskeletal therapy and cognitive behavioral therapy.',
  },
  '/cpm': {
    title: 'Cognitive & Psychological Care Management | iCCM',
    description:
      "Care management for patients with cognitive impairment and psychological conditions, including dementia, Alzheimer's and depression.",
  },
  '/contact': {
    title: 'Contact iCCM | Request a Consultation or Demo',
    description:
      'Call (844) 422-6633, email emma@integratedccm.com or visit us at 2000 Town Center suite 1900, Southfield MI. Request a consultation or demo.',
  },
};
